import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import {
  AI_RECOMMENDATION_META,
  BadgeVariant,
  StatusMeta,
} from '../../core/status/status-meta.types';
import {
  PRE_ENQUIRY_PRODUCT_META,
  PRE_REVIEW_STATUS_META,
} from '../../core/applications/pre-application.types';
import {
  KS_FUNDER_SECURITY_META,
  KS_FUNDER_TREATMENT_META,
  KS_JOURNEY_STAGE_META,
  KS_SIZING_STATE_META,
  KS_STAGE_META,
  KS_STATUS_META,
} from '../../core/kickstart/kickstart.types';
import { EMAIL_SEND_STATUS_META } from '../../core/status/email-log.types';
import {
  DF_APPROVAL_STATUS_META,
  DF_INVOICE_STATUS_META,
  DF_STAGE_META,
  DF_STATUS_META,
  DF_SUBTYPE_META,
} from '../../core/df/df.types';
import { ENTITY_STATUS_META } from '../../core/entity/entity.types';
import { TRIAGE_STATUS_META } from '../../core/feedback/admin-feedback.service';

/**
 * Renders any platform status string as a coloured chip with a human label.
 *
 * The combined lookup covers: KS ks_status / application_stage, DF df_status /
 * application_stage / invoice_status / approval_status / facility_subtype,
 * entity status, the pre-application review status, the Ops AI advisory
 * verdict, and the admin feedback-report triage status. Colour is always
 * paired with the text label, never colour alone (CapX accessibility rule).
 *
 * An unrecognised status renders its own string as a neutral chip rather than
 * blank — a missing map entry should look wrong, not look empty.
 */

const ALL_META: Record<string, StatusMeta> = {
  ...KS_STATUS_META,
  ...KS_STAGE_META,
  ...KS_SIZING_STATE_META,
  ...KS_FUNDER_SECURITY_META,
  ...KS_FUNDER_TREATMENT_META,
  ...DF_STATUS_META,
  ...DF_STAGE_META,
  ...DF_INVOICE_STATUS_META,
  ...DF_APPROVAL_STATUS_META,
  ...DF_SUBTYPE_META,
  ...EMAIL_SEND_STATUS_META,
  ...ENTITY_STATUS_META,
  ...AI_RECOMMENDATION_META,
  ...PRE_REVIEW_STATUS_META,
  ...PRE_ENQUIRY_PRODUCT_META,
  ...TRIAGE_STATUS_META,
  // FLOW-351: the status-trail event an approval's documented reason sits on.
  OFFER_ISSUED: { label: 'Offer issued', variant: 'success' },
};

/**
 * FLOW-372: the one map a chip reads when it names its `kind` — for a status that more than
 * one map uses. `SUBMITTED` is both a Kickstart stage (*Submitted*) and a
 * pre-application review status (*Submitted — under review*); in `ALL_META` the
 * later spread wins, so a Kickstart chip names its kind to read its own label.
 */
const KIND_META = {
  'ks-stage': KS_JOURNEY_STAGE_META,
} satisfies Record<string, Record<string, StatusMeta>>;

export type StatusKind = keyof typeof KIND_META;

@Component({
  selector: 'app-status-chip',
  template: `<span class="status-chip" [attr.data-variant]="variant()">{{ label() }}</span>`,
  styleUrl: './status-chip.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusChipComponent {
  readonly status = input.required<string>();
  readonly kind = input<StatusKind | null>(null);

  private readonly meta = computed<StatusMeta | undefined>(() => {
    const kind = this.kind();
    const map: Record<string, StatusMeta> = kind ? KIND_META[kind] : ALL_META;
    return map[this.status()];
  });
  protected readonly label = computed<string>(() => this.meta()?.label ?? this.status());
  protected readonly variant = computed<BadgeVariant>(() => this.meta()?.variant ?? 'neutral');
}
